// @flow
import React from 'react';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { Strong } from '@finect/tabular-core/Strong';
import { Small } from '@finect/tabular-core/Small';
import { EntityCard } from '@finect/tabular-components/EntityCard';
import { timeSince } from '@finect/front-utils/timeSince';
import { slugify } from '@finect/front-utils/slugify';
import Draft, { ContentState, convertFromRaw, convertToRaw } from 'draft-js';
import { EntryEditor } from '@finect/tabular-draft-editor';
import { OrderedSet } from 'immutable';
import type { CommentType, AuthorType } from '../../../types';
import { PendingModerationAlert } from '../..';
import { CommentDetail, CommentAuthor, CommentText } from './CommentDetail';

type Props = {
  comment: CommentType,
  author: AuthorType,
  isAuthor: boolean,
  deleteComment: Function,
  loggedIn: boolean
};

export const Comment = ({ comment, author, isAuthor, deleteComment, loggedIn }: Props) => {
  const handleDeletion = () => {
    deleteComment(comment.id);
  };

  let contentWithDate;

  if (!comment.body.content) return null;
  if (comment.body.content.includes('blocks')) {
    try {
      const contentState = convertFromRaw(JSON.parse(comment.body.content));
      const selectionState = contentState.getSelectionAfter();
      const end = contentState.getPlainText().length;
      const newContent = Draft.Modifier.insertText(
        contentState,
        selectionState.merge({
          anchorOffset: end,
          focusOffset: end
        }),
        ` – ${timeSince(new Date(comment.created))}`,
        OrderedSet.of('LIGHTCOLOR')
      );
      contentWithDate = JSON.stringify(convertToRaw(newContent));
    } catch (err) {
      contentWithDate = JSON.stringify(
        convertToRaw(
          ContentState.createFromText('No se puede mostrar el comentario en este momento, por favor vuelve más tarde.')
        )
      );
    }
  }

  const { id, type, name, alias, image, url, adviserAt: company } = author;

  const hasProfile =
    author && author.features
      ? (author.type === 'user' && author.features.includes('profile')) ||
        (author.type === 'company' && author.features.blog)
      : false;

  const nameComponent = hasProfile ? (
    <Strong>
      <a href={`https://${process.env.FINECT_BASEURL || 'www.finect.com'}${url}`}>
        {alias.length > 11 ? `${alias.slice(0, 11)}...` : alias}
      </a>
    </Strong>
  ) : (
    <Strong>{alias.length > 11 ? `${alias.slice(0, 11)}...` : alias}</Strong>
  );

  return (
    <CommentDetail isAuthor={isAuthor}>
      <CommentAuthor>
        {!isAuthor ? (
          <EntityCard
            type="profile"
            entity={{
              id,
              name,
              alias,
              icon: image,
              company: company && {
                name: company.name,
                alias: (company.url && company.url.replace('/grupos/', '')) || slugify(company.name)
              },
              adviser: type === 'adviser',
              verified: type === 'adviser' || type === 'company'
            }}
            loggedIn={loggedIn}
            arrowPosition={9}
          >
            {nameComponent}
          </EntityCard>
        ) :
          nameComponent
        }
      </CommentAuthor>

      <CommentText>
        {!comment.moderated ? <PendingModerationAlert type="comment" /> : null}

        {comment.body.content.includes('blocks') ? (
          <EntryEditor
            content={{ type: 'blocks', body: contentWithDate }}
            handleDeletion={isAuthor ? handleDeletion : null}
            decorators={['mentions', 'products', 'link', 'inlink']}
            readOnly
          />
        ) : (
          <Paragraph>
            {comment.body.content} <Small light>– {timeSince(new Date(comment.created))}</Small>
          </Paragraph>
        )}
      </CommentText>
    </CommentDetail>
  );
};
